import { useState, useEffect, useCallback, useMemo } from 'react';
import { Loader, Plus, Pencil, Trash2, Server, Globe, Users, Zap, Box, Boxes, ChevronDown, ChevronRight, TrendingUp, AlertCircle } from 'lucide-react';
import {
  PartnershipServerLink,
  SERVER_LINK_STATUSES,
  ServerLinkStatus,
  CreateServerLinkRequest,
  UpdateServerLinkRequest,
} from '../../types/partnership';
import { MinecraftServerStats } from '../../types/statusMonitor';
import { partnershipsApiService } from '../../services/partnershipsApi';
import { useMinecraftServers, getServersByCluster, usePopularity, PopularityMap } from '../../hooks/useMinecraftServers';
import { CircularProgress } from '../status/CircularProgress';
import { usernameCacheService } from '../../services/usernameCache';
import { cn } from '../../utils/cn';
import { SectionHeader } from '../ui/SectionHeader';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonLoader } from '../SkeletonLoader';

export interface PartnershipServersSectionProps {
  partnershipId: string;
  canEdit: boolean;
  canDelete: boolean;
  onUpdate: () => void;
}

const STATUS_COLORS: Record<ServerLinkStatus, string> = {
  active: 'badge-success',
  provisioning: 'badge-amber',
  decommissioned: 'badge-neutral',
};

export function PartnershipServersSection({
  partnershipId,
  canEdit,
  canDelete,
  onUpdate,
}: PartnershipServersSectionProps) {
  const minecraftServers = useMinecraftServers();
  const popularity = usePopularity();
  const [servers, setServers] = useState<PartnershipServerLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const loadServers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await partnershipsApiService.getServerLinksForPartnership(partnershipId);
      setServers(data);
      const linkedByIds = data.map((s) => s.linked_by).filter(Boolean);
      usernameCacheService.prefetchUsernames(linkedByIds);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load servers');
    } finally {
      setLoading(false);
    }
  }, [partnershipId]);

  useEffect(() => {
    loadServers();
  }, [loadServers]);

  const handleCreate = async (e: React.FormEvent, formData: CreateServerLinkRequest) => {
    e.preventDefault();
    if (!canEdit || submitting) return;
    setSubmitting(true);
    try {
      await partnershipsApiService.createServerLink(partnershipId, formData);
      setShowCreate(false);
      await loadServers();
      onUpdate();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create server link');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (linkId: string, data: UpdateServerLinkRequest) => {
    if (!canEdit || submitting) return;
    setSubmitting(true);
    try {
      await partnershipsApiService.updateServerLink(linkId, data);
      setEditingId(null);
      await loadServers();
      onUpdate();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update server link');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (linkId: string) => {
    if (!canDelete || deletingId) return;
    const confirmed = window.confirm('Are you sure you want to delete this server link?');
    if (!confirmed) return;
    setDeletingId(linkId);
    try {
      await partnershipsApiService.deleteServerLink(linkId);
      await loadServers();
      onUpdate();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete server link');
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4" aria-busy="true">
        <div className="flex items-center justify-between">
          <SkeletonLoader className="h-4 w-28" />
          <SkeletonLoader className="h-9 w-28 rounded-lg" />
        </div>
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="subcard overflow-hidden">
            <div className="flex items-center gap-2 px-3 py-2.5">
              <SkeletonLoader className="w-4 h-4 rounded" />
              <SkeletonLoader className="h-4 w-24" />
              <SkeletonLoader className="h-5 w-20 rounded-full ml-auto" />
            </div>
            <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <SkeletonLoader className="h-20 w-full" />
              <SkeletonLoader className="h-20 w-full" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <EmptyState
        bare
        compact
        tone="danger"
        icon={AlertCircle}
        title="Failed to load servers"
        description={error}
        action={
          <button type="button" onClick={loadServers} className="glass-button">
            Retry
          </button>
        }
      />
    );
  }

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Server Links"
        icon={Server}
        count={servers.length}
        actions={
          canEdit ? (
            <button
              type="button"
              onClick={() => setShowCreate(!showCreate)}
              className="glass-button"
              aria-expanded={showCreate}
            >
              <Plus className="w-4 h-4" />
              Add Server
            </button>
          ) : undefined
        }
      />

      {showCreate && canEdit && (
        <ServerCreateForm
          onSubmit={handleCreate}
          onCancel={() => setShowCreate(false)}
          submitting={submitting}
          minecraftServers={minecraftServers}
        />
      )}

      <ClusterGroupedServerList
        servers={servers}
        minecraftServers={minecraftServers}
        popularity={popularity}
        editingId={editingId}
        deletingId={deletingId}
        submitting={submitting}
        canEdit={canEdit}
        canDelete={canDelete}
        onEdit={setEditingId}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        onQuickAdd={async (clusterId, fqdn) => {
          if (!canEdit || submitting) return;
          setSubmitting(true);
          try {
            await partnershipsApiService.createServerLink(partnershipId, {
              cluster_id: clusterId,
              fqdn: fqdn || undefined,
              status: 'active',
            });
            await loadServers();
            onUpdate();
          } catch (err) {
            alert(err instanceof Error ? err.message : 'Failed to link server');
          } finally {
            setSubmitting(false);
          }
        }}
      />
    </div>
  );
}

function ClusterGroupedServerList({
  servers,
  minecraftServers,
  popularity,
  editingId,
  deletingId,
  submitting,
  canEdit,
  canDelete,
  onEdit,
  onUpdate,
  onDelete,
  onQuickAdd,
}: {
  servers: PartnershipServerLink[];
  minecraftServers: import('../../types/statusMonitor').MinecraftServersData;
  popularity: PopularityMap;
  editingId: string | null;
  deletingId: string | null;
  submitting: boolean;
  canEdit: boolean;
  canDelete: boolean;
  onEdit: (id: string | null) => void;
  onUpdate: (linkId: string, data: UpdateServerLinkRequest) => void;
  onDelete: (linkId: string) => void;
  onQuickAdd: (clusterId: string, fqdn?: string) => void;
}) {
  const [collapsedClusters, setCollapsedClusters] = useState<Set<string>>(new Set());

  const linkedClusters = useMemo(() => {
    const linkedMap = new Map<string, PartnershipServerLink[]>();
    for (const server of servers) {
      const cid = server.cluster_id;
      if (cid) {
        if (!linkedMap.has(cid)) linkedMap.set(cid, []);
        linkedMap.get(cid)!.push(server);
      }
    }

    return Array.from(linkedMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([clusterId, links]) => ({
        clusterId,
        links,
        liveInstances: getServersByCluster(minecraftServers, clusterId),
      }));
  }, [servers, minecraftServers]);

  const toggleCluster = (clusterId: string) => {
    setCollapsedClusters((prev) => {
      const next = new Set(prev);
      if (next.has(clusterId)) next.delete(clusterId);
      else next.add(clusterId);
      return next;
    });
  };

  const isOnline = (mc: MinecraftServerStats) =>
    Math.round(Date.now() - mc.last_seen * 1000) <= 180000;

  const totalOnline = (instances: MinecraftServerStats[]) =>
    instances.filter(isOnline).length;

  const totalPlayers = (instances: MinecraftServerStats[]) =>
    instances.reduce((sum, i) => (isOnline(i) ? sum + i.playerCount : sum), 0);

  const getClusterPopularity = (links: PartnershipServerLink[]) => {
    const matches: { fqdn: string; index: number; rank: number }[] = [];
    for (const link of links) {
      if (!link.fqdn) continue;
      const entry = popularity.get(link.fqdn.toLowerCase());
      if (entry) matches.push({ fqdn: link.fqdn, ...entry });
    }
    return matches.sort((a, b) => a.rank - b.rank);
  };

  const getUnlinkedFqdns = (links: PartnershipServerLink[]) => {
    const linkedFqdns = new Set(links.map((l) => l.fqdn?.toLowerCase()).filter(Boolean));
    const fqdnPrefixes: string[] = [];
    for (const link of links) {
      if (!link.fqdn) continue;
      const base = link.fqdn.toLowerCase().replace(/\.playcdu\.co$/, '');
      const parts = base.split('.');
      if (parts.length > 1) {
        fqdnPrefixes.push(parts[0]);
      } else {
        fqdnPrefixes.push(base);
      }
    }
    if (fqdnPrefixes.length === 0 && links.length > 0 && links[0].cluster_id) {
      fqdnPrefixes.push(links[0].cluster_id.toLowerCase());
    }
    const unlinked: { fqdn: string; index: number; rank: number }[] = [];
    for (const [fqdn, entry] of popularity.entries()) {
      if (linkedFqdns.has(fqdn)) continue;
      const base = fqdn.replace(/\.playcdu\.co$/, '');
      const prefix = base.split('.')[0];
      if (fqdnPrefixes.includes(prefix)) {
        unlinked.push({ fqdn, ...entry });
      }
    }
    return unlinked.sort((a, b) => a.rank - b.rank);
  };

  if (servers.length === 0) {
    return (
      <EmptyState
        bare
        compact
        icon={Server}
        title="No server links yet"
        description="Link the clusters and servers this partnership runs on."
      />
    );
  }

  return (
    <div className="space-y-3">
      {linkedClusters.map(({ clusterId, links, liveInstances }) => {
        const isCollapsed = collapsedClusters.has(clusterId);
        const hasLive = liveInstances.length > 0;
        const online = totalOnline(liveInstances);
        const players = totalPlayers(liveInstances);
        const clusterPop = getClusterPopularity(links);
        const bestPop = clusterPop.length > 0 ? clusterPop[0] : null;
        const unlinkedFqdns = getUnlinkedFqdns(links);

        return (
          <div key={clusterId} className="subcard overflow-hidden">
            <button
              type="button"
              onClick={() => toggleCluster(clusterId)}
              className="w-full flex items-center justify-between gap-2 px-3 py-2.5 hover:bg-glass-200/5 transition-colors text-left"
              aria-expanded={!isCollapsed}
            >
              <div className="flex items-center gap-2 min-w-0">
                {isCollapsed ? <ChevronRight className="w-4 h-4 text-glass-400 shrink-0" /> : <ChevronDown className="w-4 h-4 text-glass-400 shrink-0" />}
                <Server className="w-4 h-4 text-accent-300 shrink-0" />
                <span className="font-mono font-medium text-white text-sm truncate">{clusterId}</span>
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                {bestPop && (
                  <span className="badge badge-purple" title={`#${bestPop.rank} — ${bestPop.fqdn}`}>
                    <TrendingUp className="w-3 h-3" />
                    #{bestPop.rank} · {Math.round(bestPop.index)}
                  </span>
                )}
                <span className={cn(
                  'badge badge-dot',
                  hasLive
                    ? online === liveInstances.length ? 'badge-success'
                      : online > 0 ? 'badge-amber'
                      : 'badge-danger'
                    : 'badge-neutral'
                )}>
                  {hasLive ? `${online}/${liveInstances.length} online` : 'offline'}
                </span>
                {hasLive && players > 0 && (
                  <span className="text-xs text-glass-400 flex items-center gap-1 tabular-nums">
                    <Users className="w-3 h-3" /> {players}
                  </span>
                )}
              </div>
            </button>

            {!isCollapsed && (
              <div className="p-3 border-t border-glass-200/10 space-y-2">
                {/* Linked server rows */}
                {links.map((server) => {
                  if (editingId === server.id && canEdit) {
                    return (
                      <div key={server.id} className="p-3 rounded-lg bg-dark-900/30 border border-glass-200/10">
                        <ServerEditForm
                          server={server}
                          onSave={(data) => onUpdate(server.id, data)}
                          onCancel={() => onEdit(null)}
                          submitting={submitting}
                        />
                      </div>
                    );
                  }
                  const pop = server.fqdn ? popularity.get(server.fqdn.toLowerCase()) : undefined;
                  return (
                    <div key={server.id} className="flex items-center justify-between gap-2 flex-wrap px-1 py-0.5">
                      <div className="flex items-center gap-2 flex-wrap text-xs">
                        {server.fqdn && (
                          <span className="inline-flex items-center gap-1 font-mono text-glass-300">
                            <Globe className="w-3 h-3 text-glass-500" />
                            {server.fqdn}
                          </span>
                        )}
                        {server.server_name && <span className="text-glass-400">{server.server_name}</span>}
                        <span className={cn('badge badge-dot', STATUS_COLORS[server.status])}>{server.status}</span>
                        {pop && (
                          <span className="badge badge-purple">
                            <TrendingUp className="w-3 h-3" />#{pop.rank} · {Math.round(pop.index)}
                          </span>
                        )}
                        {server.notes && <span className="text-glass-500 italic truncate max-w-[180px]" title={server.notes}>{server.notes}</span>}
                      </div>
                      <div className="flex items-center gap-0.5 flex-shrink-0">
                        {canEdit && (
                          <button type="button" onClick={() => onEdit(server.id)} className="btn-icon btn-icon-sm" title="Edit" aria-label="Edit server link"><Pencil className="w-3.5 h-3.5" /></button>
                        )}
                        {canDelete && (
                          <button type="button" onClick={() => onDelete(server.id)} disabled={deletingId === server.id}
                            className={cn('btn-icon btn-icon-sm text-red-400 hover:text-red-300', deletingId === server.id && 'opacity-50')} title="Delete" aria-label="Delete server link">
                            {deletingId === server.id ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Unlinked FQDNs in this cluster — quick add */}
                {canEdit && unlinkedFqdns.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5 px-1 pt-1">
                    {unlinkedFqdns.map(({ fqdn, rank, index }) => (
                      <button
                        key={fqdn}
                        type="button"
                        onClick={() => onQuickAdd(clusterId, fqdn)}
                        className="chip border-dashed hover:text-white hover:border-accent-500/40 hover:bg-accent-500/5 transition-colors"
                        title={`Add ${fqdn} — Popularity #${rank} (${Math.round(index)})`}
                      >
                        <Plus className="w-2.5 h-2.5" />
                        <span className="font-mono">{fqdn}</span>
                        <span className="text-purple-300">#{rank}</span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Live instances */}
                {hasLive ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                    {liveInstances.map((mc) => (
                      <LiveInstanceCard key={`${mc.clusterId}-${mc.serverId}`} stats={mc} />
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 py-2 px-1">
                    <div className="w-2 h-2 rounded-full bg-glass-400 flex-shrink-0" />
                    <span className="text-sm text-glass-500">All instances offline — not reporting to status monitor</span>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ServerCreateForm({
  onSubmit,
  onCancel,
  submitting,
  minecraftServers,
}: {
  onSubmit: (e: React.FormEvent, data: CreateServerLinkRequest) => void;
  onCancel: () => void;
  submitting: boolean;
  minecraftServers: import('../../types/statusMonitor').MinecraftServersData;
}) {
  const [fqdn, setFqdn] = useState('');
  const [clusterId, setClusterId] = useState('');
  const [serverName, setServerName] = useState('');
  const [status, setStatus] = useState<ServerLinkStatus>('active');
  const [notes, setNotes] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  const allClusterIds = useMemo(() => {
    const ids = new Set<string>();
    for (const srv of Object.values(minecraftServers)) {
      if (srv.clusterId) ids.add(srv.clusterId);
    }
    return Array.from(ids).sort((a, b) => a.localeCompare(b));
  }, [minecraftServers]);

  const filteredSuggestions = useMemo(() => {
    if (!clusterId.trim()) return allClusterIds;
    const q = clusterId.toLowerCase();
    return allClusterIds.filter((id) => id.toLowerCase().includes(q));
  }, [clusterId, allClusterIds]);

  const matchedInstances = useMemo(() => {
    if (!clusterId.trim()) return [];
    return getServersByCluster(minecraftServers, clusterId.trim());
  }, [clusterId, minecraftServers]);

  const handleSelectCluster = (id: string) => {
    setClusterId(id);
    setShowSuggestions(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!clusterId.trim()) return;
    onSubmit(e, {
      fqdn: fqdn.trim() || undefined,
      cluster_id: clusterId.trim(),
      server_name: serverName.trim() || undefined,
      status,
      notes: notes.trim() || undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="subcard p-4 space-y-3">
      <div className="relative">
        <label className="block text-xs font-medium text-glass-400 mb-1.5">Cluster ID *</label>
        <input
          type="text"
          value={clusterId}
          onChange={(e) => {
            setClusterId(e.target.value);
            setShowSuggestions(true);
          }}
          onFocus={() => setShowSuggestions(true)}
          placeholder="Type to search live clusters…"
          className="glass-input w-full font-mono"
          required
          autoComplete="off"
        />
        {showSuggestions && filteredSuggestions.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-1 glass-card overflow-hidden z-10 max-h-48 overflow-y-auto py-1">
            {filteredSuggestions.map((id) => {
              const instances = getServersByCluster(minecraftServers, id);
              const onlineCount = instances.filter(
                (i) => Math.round(Date.now() - i.last_seen * 1000) <= 180000
              ).length;
              const playerCount = instances.reduce((sum, i) => {
                if (Math.round(Date.now() - i.last_seen * 1000) > 180000) return sum;
                return sum + i.playerCount;
              }, 0);
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => handleSelectCluster(id)}
                  className={cn(
                    'w-full text-left px-3 py-2 hover:bg-glass-200/10 transition-colors flex items-center justify-between gap-2',
                    id === clusterId.trim() && 'bg-accent-500/10'
                  )}
                >
                  <div className="flex items-center gap-2">
                    <Server className="w-3.5 h-3.5 text-glass-400" />
                    <span className="text-sm font-mono text-white">{id}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={cn(
                      'badge badge-dot',
                      onlineCount === instances.length
                        ? 'badge-success'
                        : onlineCount > 0
                        ? 'badge-amber'
                        : 'badge-danger'
                    )}>
                      {onlineCount}/{instances.length}
                    </span>
                    {playerCount > 0 && (
                      <span className="text-xs text-glass-400 tabular-nums">{playerCount}p</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Live cluster preview */}
      {matchedInstances.length > 0 && (
        <div className="alert alert-info flex-col gap-1.5">
          <p className="text-xs font-medium flex items-center gap-1">
            <Zap className="w-3 h-3" />
            Matched {matchedInstances.length} live instance{matchedInstances.length !== 1 ? 's' : ''}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {matchedInstances.map((mc) => {
              const offline = Math.round(Date.now() - mc.last_seen * 1000) > 180000;
              return (
                <span
                  key={`${mc.clusterId}-${mc.serverId}`}
                  className={cn(
                    'chip',
                    offline
                      ? 'border-red-500/30 bg-red-500/10 text-red-300'
                      : 'text-glass-200'
                  )}
                >
                  <span className={cn(
                    'w-1.5 h-1.5 rounded-full flex-shrink-0',
                    offline ? 'bg-red-500' : 'bg-green-500'
                  )} />
                  <span className="font-mono">{mc.serverId}</span>
                  {!offline && (
                    <span className="text-glass-500 tabular-nums">
                      {mc.playerCount}p · {mc.tps.toFixed(0)}tps
                    </span>
                  )}
                </span>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-glass-400 mb-1.5">FQDN</label>
          <input
            type="text"
            value={fqdn}
            onChange={(e) => setFqdn(e.target.value)}
            placeholder="server.example.com (optional)"
            className="glass-input w-full font-mono"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-glass-400 mb-1.5">Server Name</label>
          <input
            type="text"
            value={serverName}
            onChange={(e) => setServerName(e.target.value)}
            placeholder="Optional"
            className="glass-input w-full"
          />
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-glass-400 mb-1.5">Status</label>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as ServerLinkStatus)}
          className="glass-select w-full capitalize"
        >
          {SERVER_LINK_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-xs font-medium text-glass-400 mb-1.5">Notes</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional"
          rows={2}
          className="glass-input w-full resize-none"
        />
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="glass-button-secondary">
          Cancel
        </button>
        <button type="submit" disabled={submitting} className="btn-primary">
          {submitting ? <Loader className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Create
        </button>
      </div>
    </form>
  );
}

function LiveInstanceCard({ stats }: { stats: MinecraftServerStats }) {
  const timeSinceLastSeen = Math.round(Date.now() - stats.last_seen * 1000);
  const isOffline = timeSinceLastSeen > 180000;
  const identifier = `${stats.clusterId} - ${stats.serverId}`;

  const tpsColor =
    isOffline ? '#4B5563' :
    stats.tps >= 18 ? '#60A5FA' :
    stats.tps >= 15 ? '#A78BFA' :
    stats.tps >= 10 ? '#F472B6' : '#EF4444';

  const getModloaderColor = (modloader: string) => {
    switch (modloader.toLowerCase()) {
      case 'forge': return 'text-purple-400';
      case 'neoforge': return 'text-indigo-400';
      case 'fabric': return 'text-fuchsia-400';
      default: return 'text-gray-400';
    }
  };

  const formatDowntime = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  const popScore = !isOffline ? (stats.popularity?.popularity_index ?? null) : null;

  return (
    <div className={cn(
      'rounded-lg border p-2.5 bg-dark-900/30',
      isOffline ? 'border-red-500/30' : 'border-glass-200/10'
    )}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <div className={cn(
            'w-1.5 h-1.5 rounded-full flex-shrink-0',
            isOffline ? 'bg-red-500' : 'bg-green-500'
          )} />
          <span className="text-xs font-medium text-white truncate">{identifier}</span>
          {popScore !== null && popScore > 0 && (
            <span className="badge badge-purple" title={`Popularity index: ${popScore.toFixed(1)}`}>
              <TrendingUp className="w-2.5 h-2.5" />
              {Math.round(popScore)}
            </span>
          )}
        </div>
        <span className={cn(
          'badge',
          isOffline ? 'badge-danger' : 'badge-success'
        )}>
          {isOffline ? formatDowntime(timeSinceLastSeen) : 'Online'}
        </span>
      </div>

      {/* Metrics grid */}
      <div className="flex items-center gap-3">
        {/* TPS ring */}
        <CircularProgress
          value={isOffline ? 0 : stats.tps}
          color={tpsColor}
          maxValue={20}
          size="w-8 h-8"
          label={isOffline ? '?' : stats.tps.toFixed(1)}
          showPercentage={false}
        />

        {/* Stats */}
        <div className="flex-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px]">
          <div className="flex items-center justify-between">
            <span className="text-glass-500 flex items-center gap-0.5">
              <Users className="w-2.5 h-2.5" /> Players
            </span>
            <span className="font-medium text-white tabular-nums">
              {isOffline ? '?' : `${stats.playerCount}/${stats.maxPlayers}`}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-glass-500 flex items-center gap-0.5">
              <Zap className="w-2.5 h-2.5" /> MSPT
            </span>
            <span className="font-medium text-white tabular-nums">
              {isOffline ? '?' : `${stats.mspt.toFixed(1)}ms`}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-glass-500 flex items-center gap-0.5">
              <Boxes className="w-2.5 h-2.5" /> Entities
            </span>
            <span className="font-medium text-white tabular-nums">
              {isOffline ? '?' : `${(stats.entities / 1000).toFixed(1)}k`}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-glass-500 flex items-center gap-0.5">
              <Box className="w-2.5 h-2.5" /> Chunks
            </span>
            <span className="font-medium text-white tabular-nums">
              {isOffline ? '?' : `${(stats.chunks / 1000).toFixed(1)}k`}
            </span>
          </div>
        </div>
      </div>

      {/* Footer: modloader + MC version */}
      <div className="flex items-center justify-between mt-2 px-0.5 text-[11px]">
        <div className="flex items-center gap-1">
          <span className={cn('font-medium', getModloaderColor(stats.modloader))}>
            {stats.modloader}
          </span>
          <span className="text-glass-500 tabular-nums">{stats.modloaderVersion}</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-glass-500">MC</span>
          <span className="font-medium text-white tabular-nums">{isOffline ? '?' : stats.mcVersion}</span>
        </div>
      </div>
    </div>
  );
}

function ServerEditForm({
  server,
  onSave,
  onCancel,
  submitting,
}: {
  server: PartnershipServerLink;
  onSave: (data: UpdateServerLinkRequest) => void;
  onCancel: () => void;
  submitting: boolean;
}) {
  const [fqdn, setFqdn] = useState(server.fqdn || '');
  const [clusterId, setClusterId] = useState(server.cluster_id || '');
  const [serverName, setServerName] = useState(server.server_name || '');
  const [status, setStatus] = useState<ServerLinkStatus>(server.status);
  const [notes, setNotes] = useState(server.notes || '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!clusterId.trim()) return;
    onSave({
      fqdn: fqdn.trim() || null,
      cluster_id: clusterId.trim(),
      server_name: serverName.trim() || null,
      status,
      notes: notes.trim() || null,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-glass-400 mb-1.5">Cluster ID *</label>
          <input
            type="text"
            value={clusterId}
            onChange={(e) => setClusterId(e.target.value)}
            className="glass-input w-full font-mono"
            required
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-glass-400 mb-1.5">FQDN</label>
          <input
            type="text"
            value={fqdn}
            onChange={(e) => setFqdn(e.target.value)}
            className="glass-input w-full font-mono"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-glass-400 mb-1.5">Server Name</label>
          <input
            type="text"
            value={serverName}
            onChange={(e) => setServerName(e.target.value)}
            className="glass-input w-full"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-glass-400 mb-1.5">Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as ServerLinkStatus)}
            className="glass-select w-full capitalize"
          >
            {SERVER_LINK_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-glass-400 mb-1.5">Notes</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="glass-input w-full resize-none"
        />
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="glass-button-secondary">
          Cancel
        </button>
        <button type="submit" disabled={submitting} className="btn-primary min-w-[5rem]">
          {submitting ? 'Saving...' : 'Save'}
        </button>
      </div>
    </form>
  );
}
